import { useState } from "react";
import { Plus, MessageSquare, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const PROVIDER_COLORS = {
  openai: "#10B981",
  anthropic: "#F5DF4D",
  groq: "#3B82F6",
  openrouter: "#A78BFA",
};

export default function Sidebar({
  chats,
  providers,
  activeChat,
  onSelectChat,
  onNewChat,
  onDeleteChat,
}) {
  const [expandedProviders, setExpandedProviders] = useState({});

  const grouped = {};
  for (const chat of chats) {
    const key = chat.provider_id || "unknown";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(chat);
  }

  const toggleProvider = (pid) => {
    setExpandedProviders((prev) => ({ ...prev, [pid]: !prev[pid] }));
  };

  const getProviderInfo = (providerId) => providers.find((p) => p.id === providerId);

  return (
    <div
      data-testid="sidebar"
      className="w-[260px] min-w-[260px] h-screen flex flex-col border-r border-[#273142] bg-[#080B10]"
    >
      {/* Header with Logo */}
      <div className="p-4 border-b border-[#273142]">
        <div className="flex items-center gap-2.5 mb-4">
          <img src="/logo.png" alt="Anymind" className="w-7 h-7 rounded-lg" />
          <span className="text-sm font-semibold tracking-tight">ANYMIND</span>
        </div>
        <button
          data-testid="new-chat-button"
          onClick={onNewChat}
          className="w-full bg-[#E5FF00] text-[#080B10] font-semibold uppercase tracking-wider text-xs px-4 py-2.5 hover:bg-[#D4EC00] transition-colors duration-200 rounded-lg flex items-center justify-center gap-2"
        >
          <Plus size={16} />
          NEW CHAT
        </button>
      </div>

      {/* Chat List */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1">
          {Object.entries(grouped).map(([providerId, providerChats]) => {
            const pInfo = getProviderInfo(providerId);
            const isExpanded = expandedProviders[providerId] !== false;
            const color = PROVIDER_COLORS[pInfo?.provider] || "#94A3B8";

            return (
              <div key={providerId}>
                <button
                  data-testid={`provider-group-${providerId}`}
                  onClick={() => toggleProvider(providerId)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-mono uppercase tracking-[0.05em] text-[#94A3B8] hover:text-white hover:bg-[#1C2433] transition-colors rounded-md"
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <span className="truncate flex-1">{pInfo?.display_name || "Unknown"}</span>
                  <span className="text-[#475569] text-[10px] font-mono">{providerChats.length}</span>
                </button>
                {isExpanded &&
                  providerChats.map((chat) => (
                    <div
                      key={chat.id}
                      data-testid={`chat-item-${chat.id}`}
                      onClick={() => onSelectChat(chat)}
                      className={`group flex items-center gap-2 px-3 py-2.5 ml-2 cursor-pointer transition-all rounded-md ${ activeChat?.id === chat.id
                          ? "bg-[#1C2433] border-l-2 border-l-[#E5FF00]"
                          : "border-l-2 border-l-transparent hover:bg-[#111620]"
                      }`}
                    >
                      <MessageSquare size={14} className="text-[#475569] flex-shrink-0" />
                      <span className="text-xs truncate flex-1 text-[#E0E0E0]">{chat.title}</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              data-testid={`delete-chat-${chat.id}`}
                              onClick={(e) => { e.stopPropagation(); onDeleteChat(chat.id); }}
                              className="opacity-0 group-hover:opacity-100 p-1 text-[#475569] hover:text-[#FF3366] transition-all rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={4}><p className="text-xs">Delete</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  ))}
              </div>
            );
          })}
          {chats.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#1C2433] border border-[#273142] mb-3">
                <MessageSquare size={18} className="text-[#E5FF00]" />
              </div>
              <p className="text-[#E5FF00] font-mono text-[10px] uppercase tracking-wider mb-1">No chats</p>
              <p className="text-[#475569] text-xs">Add a provider to start</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
